import { createContext, useContext, useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const userData = useSelector((state) => state.auth?.userData);
  const accessToken = useSelector((state) => {
    try {
      const profile = localStorage.getItem("profile");
      if (profile) {
        return JSON.parse(profile)?.accessToken;
      }
    } catch (error) {
      return null;
    }
    return null;
  });

  useEffect(() => {
    // Only connect if user is authenticated
    if (!accessToken || !userData) {
      // Disconnect if socket exists but user logged out
      if (socketRef.current) {
        socketRef.current.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Connect to Socket.io server
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";
    const newSocket = io(API_URL, {
      auth: {
        token: accessToken,
      },
      transports: ["websocket", "polling"],
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection event handlers
    newSocket.on("connect", () => {
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setIsConnected(false);
    });

    // Cleanup on unmount or when user logs out
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [accessToken, userData]);

  const value = {
    socket,
    isConnected,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
